import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Plus, Trash, PencilSimple, Check, Flask } from "@phosphor-icons/react";
import { Card, Stat, money, fmtDate, errToast, today, NumField, SelectField } from "@/components/finance/fin";

export function BalanceChart({ schedule }) {
  const step = Math.max(1, Math.floor((schedule || []).length / 120));
  const data = (schedule || []).filter((_, i) => i % step === 0).map((r) => ({ date: r.date.slice(0, 7), balance: r.balance }));
  return (
    <div className="h-56" data-testid="fin-balance-chart">
      <ResponsiveContainer>
        <AreaChart data={data}>
          <defs><linearGradient id="bal" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5} /><stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={40} stroke="hsl(var(--muted-foreground))" />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} stroke="hsl(var(--muted-foreground))" width={52} />
          <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
          <Area type="monotone" dataKey="balance" stroke="hsl(var(--primary))" fill="url(#bal)" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ScheduleTable({ schedule }) {
  const [page, setPage] = useState(0);
  const size = 24;
  const rows = schedule || [];
  const pages = Math.max(1, Math.ceil(rows.length / size));
  return (
    <Card title="Amortization schedule" subtitle={`${rows.length} payments`} testId="fin-schedule"
      right={<div className="flex items-center gap-2 text-sm">
        <Button size="sm" variant="outline" disabled={page === 0} data-testid="fin-schedule-prev" onClick={() => setPage(page - 1)}>Prev</Button>
        <span className="text-muted-foreground">{page + 1}/{pages}</span>
        <Button size="sm" variant="outline" disabled={page >= pages - 1} data-testid="fin-schedule-next" onClick={() => setPage(page + 1)}>Next</Button>
      </div>}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-muted-foreground text-left"><tr><th className="py-2">#</th><th>Date</th><th className="text-right">Payment</th><th className="text-right">Interest</th><th className="text-right">Principal</th><th className="text-right">Extra</th><th className="text-right">Balance</th></tr></thead>
          <tbody>
            {rows.slice(page * size, page * size + size).map((r) => (
              <tr key={r.n} className="border-t border-border/60">
                <td className="py-1.5 text-muted-foreground">{r.n}</td><td>{fmtDate(r.date)}</td><td className="text-right">{money(r.payment)}</td>
                <td className="text-right">{money(r.interest)}</td><td className="text-right">{money(r.principal)}</td>
                <td className="text-right text-emerald-400">{r.extra ? money(r.extra) : ""}</td><td className="text-right font-semibold">{money(r.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function AcceleratorStats({ acc, balanceToday }) {
  const ts = acc.time_saved;
  return (
    <Card title="Payoff accelerator" subtitle="Based on your recorded extra principal payments. Your regular payment stays the same." testId="fin-accelerator">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Previous payoff" value={fmtDate(acc.previous_payoff_date)} testId="fin-prev-payoff" />
        <Stat label="New payoff" value={fmtDate(acc.new_payoff_date)} tone="good" testId="fin-new-payoff" />
        <Stat label="Time saved" value={`${ts.years}y ${ts.months}m`} tone={acc.months_saved ? "good" : undefined} testId="fin-time-saved" />
        <Stat label="Interest saved" value={money(acc.interest_saved)} tone={acc.interest_saved > 0 ? "good" : undefined} testId="fin-interest-saved" />
        <Stat label="Total extra principal" value={money(acc.total_extra_principal)} testId="fin-total-extra" />
        <Stat label="Est. balance today" value={money(balanceToday)} testId="fin-balance-today" />
        <Stat label="Balance after prepayments" value={money(acc.current.balance_after_prepayments)} testId="fin-balance-after" />
        <Stat label="Final payment" value={`${money(acc.current.final_payment)}`} sub={fmtDate(acc.new_payoff_date)} testId="fin-final-payment" />
      </div>
    </Card>
  );
}

function TxRow({ t, pid, cards, onChanged, allowanceField }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(t);
  const save = async () => {
    try { await api.put(`/finance/profiles/${pid}/transactions/${t.id}`, { ...v, amount: Number(v.amount) }); setEdit(false); onChanged(); } catch (e) { errToast(e); }
  };
  const del = async () => {
    if (!window.confirm("Delete this payment?")) return;
    try { await api.delete(`/finance/profiles/${pid}/transactions/${t.id}`); onChanged(); } catch (e) { errToast(e); }
  };
  return (
    <tr className="border-t border-border/60" data-testid={`fin-tx-${t.id}`}>
      <td className="py-2">{edit ? <input type="date" value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} className="h-8 rounded border border-input bg-secondary/40 px-2" /> : fmtDate(t.date)}</td>
      <td>{edit ? <input type="number" value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value })} className="h-8 w-28 rounded border border-input bg-secondary/40 px-2" /> : money(t.amount)}</td>
      {cards && <td>{cards.find((c) => c.id === t.card_id)?.name || "—"}</td>}
      {allowanceField && <td className="text-xs">{t.counts_toward_allowance ? "Counts" : "Excluded"}</td>}
      <td className="text-muted-foreground text-xs">{t.note}</td>
      <td className="text-right whitespace-nowrap">
        {edit ? <Button size="sm" variant="ghost" data-testid={`fin-tx-save-${t.id}`} onClick={save}><Check className="h-4 w-4" /></Button>
          : <Button size="sm" variant="ghost" data-testid={`fin-tx-edit-${t.id}`} onClick={() => setEdit(true)}><PencilSimple className="h-4 w-4" /></Button>}
        <Button size="sm" variant="ghost" data-testid={`fin-tx-delete-${t.id}`} onClick={del}><Trash className="h-4 w-4 text-red-400" /></Button>
      </td>
    </tr>
  );
}

/** Recorded (actual) payments — permanent, editable history attached to a saved profile. */
export function PaymentHistory({ pid, txns, title = "Recorded extra principal payments", cards, allowanceField, onProposed }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ date: today(), amount: "", note: "", card_id: cards?.[0]?.id, counts_toward_allowance: true });
  const changed = () => { qc.invalidateQueries({ queryKey: ["fin-profile", pid] }); qc.invalidateQueries({ queryKey: ["fin-summary"] }); };
  const add = async () => {
    try {
      await api.post(`/finance/profiles/${pid}/transactions`, { ...f, amount: Number(f.amount) });
      setF({ ...f, amount: "", note: "", counts_toward_allowance: true }); onProposed?.(0); changed();
    } catch (e) { errToast(e); }
  };
  if (!pid) return <Card title={title} testId="fin-history"><p className="text-sm text-muted-foreground" data-testid="fin-history-save-first">Save this calculation to keep a permanent history of actual payments.</p></Card>;
  return (
    <Card title={title} subtitle="Actual payments you made. Everything recalculates after each change." testId="fin-history">
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <NumField id="fin-tx-date" label="Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} />
        <NumField id="fin-tx-amount" label="Amount" value={f.amount} onChange={(v) => { setF({ ...f, amount: v }); onProposed?.(Number(v) || 0); }} />
        {cards && <SelectField id="fin-tx-card" label="Card" value={f.card_id} onChange={(v) => setF({ ...f, card_id: v })} options={cards.map((c) => [c.id, c.name])} />}
        <NumField id="fin-tx-note" label="Note" type="text" value={f.note} onChange={(v) => setF({ ...f, note: v })} />
        {allowanceField && <label className="flex items-center gap-2 text-xs pb-2"><input type="checkbox" data-testid="fin-tx-counts" checked={f.counts_toward_allowance} onChange={(e) => setF({ ...f, counts_toward_allowance: e.target.checked })} /> Counts toward annual allowance</label>}
        <Button data-testid="fin-tx-add-btn" onClick={add} disabled={!f.amount}><Plus className="h-4 w-4 mr-1" /> Record payment</Button>
      </div>
      {txns.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="fin-history-empty">No payments recorded yet.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="text-xs uppercase text-muted-foreground text-left"><tr><th className="py-1">Date</th><th>Amount</th>{cards && <th>Card</th>}{allowanceField && <th>Allowance</th>}<th>Note</th><th /></tr></thead>
          <tbody>{[...txns].reverse().map((t) => <TxRow key={`${t.id}-${t.amount}-${t.date}`} t={t} pid={pid} cards={cards} allowanceField={allowanceField} onChanged={changed} />)}</tbody>
        </table></div>
      )}
    </Card>
  );
}

const REC = [["once", "One time"], ["weekly", "Every week"], ["biweekly", "Every 2 weeks"], ["monthly", "Every month"], ["annually", "Every year"]];

/** Simulated (what-if) extra payments — stored as a plan, never counted as actual payments. */
export function PlannedExtras({ list, onChange, sim }) {
  const set = (i, k, v) => onChange(list.map((e, j) => (j === i ? { ...e, [k]: v } : e)));
  return (
    <Card title="What-if extra payments" subtitle="Simulated only — not recorded as actual payments." testId="fin-planned"
      right={<span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-400"><Flask className="h-4 w-4" /> Simulation</span>}>
      {list.map((e, i) => (
        <div key={i} className="flex flex-wrap gap-2 items-end mb-2">
          <NumField id={`fin-plan-amount-${i}`} label="Amount" value={e.amount} onChange={(v) => set(i, "amount", v)} />
          <SelectField id={`fin-plan-rec-${i}`} label="How often" value={e.recurrence} onChange={(v) => set(i, "recurrence", v)} options={REC} />
          <NumField id={`fin-plan-date-${i}`} label="Starting" type="date" value={e.date} onChange={(v) => set(i, "date", v)} />
          <Button variant="ghost" size="sm" data-testid={`fin-plan-remove-${i}`} onClick={() => onChange(list.filter((_, j) => j !== i))}><Trash className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" data-testid="fin-plan-add" onClick={() => onChange([...list, { amount: 100, recurrence: "monthly", date: today() }])}><Plus className="h-4 w-4 mr-1" /> Add what-if payment</Button>
      {sim && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4" data-testid="fin-sim-results">
          <Stat label="Simulated payoff" value={fmtDate(sim.new_payoff_date)} tone="good" />
          <Stat label="Simulated time saved" value={`${sim.time_saved.years}y ${sim.time_saved.months}m`} />
          <Stat label="Simulated interest saved" value={money(sim.interest_saved)} tone="good" />
          <Stat label="Total extra (actual + simulated)" value={money(sim.total_extra_principal)} />
        </div>
      )}
    </Card>
  );
}
